/**
 * DictationWaveform
 *
 * Sleek inline audio waveform visualization.
 * 400 thin bars with opacity modulation spanning full width.
 * Processing state shows "Dictating..." with dot animation.
 * Boot-up state shows 5-circle ripple loader.
 */

import React, { useEffect, useRef, useState } from 'react';

interface DictationWaveformProps {
  /** Current audio level (0.0-1.0) - fallback if no frequencyData. */
  audioLevel: number;
  /** Frequency spectrum data (Uint8Array, 0-255 per bin) for detailed bars. */
  frequencyData: Uint8Array | undefined;
  /** Whether transcription is in progress (shows "Dictating..."). */
  isProcessing: boolean;
  /** Whether microphone is booting up (shows circle loader). */
  isBooting?: boolean;
}

const BAR_COUNT = 400;
const SAMPLE_RATE = 24000;
const FFT_SIZE = 2048;
const HZ_PER_BIN = SAMPLE_RATE / FFT_SIZE;

/** Map bar index to a frequency range (logarithmic spread across 80-8000 Hz). */
function getFreqRange(index: number): [number, number] {
  const logMin = Math.log(80);
  const logMax = Math.log(8000);
  const step = (logMax - logMin) / BAR_COUNT;
  return [Math.exp(logMin + step * index), Math.exp(logMin + step * (index + 1))];
}

export const DictationWaveform: React.FC<DictationWaveformProps> = ({
  audioLevel,
  frequencyData,
  isProcessing,
  isBooting = false,
}) => {
  const [bars, setBars] = useState<number[]>(() => Array(BAR_COUNT).fill(0.08));
  const targetBarsRef = useRef<number[]>(Array(BAR_COUNT).fill(0.08));
  const animFrameRef = useRef<number | null>(null);
  const smoothBarsRef = useRef<number[]>(Array(BAR_COUNT).fill(0.08));

  // Compute target bar heights from frequency data or audio level
  useEffect(() => {
    if (isProcessing) return;

    if (frequencyData && frequencyData.length > 0) {
      const newTargets: number[] = [];
      for (let i = 0; i < BAR_COUNT; i++) {
        const [lowHz, highHz] = getFreqRange(i);
        const lowBin = Math.max(0, Math.floor(lowHz / HZ_PER_BIN));
        const highBin = Math.min(Math.ceil(highHz / HZ_PER_BIN), frequencyData.length - 1);
        let sum = 0;
        let count = 0;
        for (let bin = lowBin; bin <= highBin; bin++) {
          sum += frequencyData[bin];
          count++;
        }
        const avg = count > 0 ? sum / count : 0;
        newTargets.push(Math.max(0.06, (avg / 255) * 0.95));
      }
      targetBarsRef.current = newTargets;
    } else {
      // Fallback: subtle sine wave modulated by audio level
      const newTargets: number[] = [];
      const time = Date.now() * 0.002;
      for (let i = 0; i < BAR_COUNT; i++) {
        const pos = i / BAR_COUNT;
        const wave = Math.sin(pos * Math.PI * 3.5 + time) * 0.35 + 0.5;
        const height = 0.06 + audioLevel * 2.2 * wave;
        newTargets.push(Math.min(1, Math.max(0.06, height)));
      }
      targetBarsRef.current = newTargets;
    }
  }, [frequencyData, audioLevel, isProcessing]);

  // Smooth animation loop with asymmetric attack/decay
  useEffect(() => {
    if (isProcessing) {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      return;
    }

    const animate = () => {
      const current = smoothBarsRef.current;
      const targets = targetBarsRef.current;
      const newBars = current.map((val, i) => {
        const target = targets[i];
        // Faster attack, slower decay for natural feel
        const smoothing = target > val ? 0.22 : 0.1;
        return val + (target - val) * smoothing;
      });
      smoothBarsRef.current = newBars;
      setBars(newBars);
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [isProcessing]);

  // Boot-up state: 5-circle ripple loader (centered in chat area) + "Service starting up" text
  if (isBooting) {
    return (
      <div className="dictation-waveform-container dictation-center-state">
        <div className="dictation-boot-wrapper">
          <div className="dictation-loader">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="dictation-loader-circle">
                <div className="dictation-loader-dot" />
                <div className="dictation-loader-outline" />
              </div>
            ))}
          </div>
          <span className="dictation-boot-text">Service starting up</span>
        </div>
      </div>
    );
  }

  // Processing state: "Dictating..." with animated dots (centered in chat area)
  if (isProcessing) {
    return (
      <div className="dictation-waveform-container dictation-center-state">
        <span className="dictation-status-text">
          Dictating<span className="dictation-dots"><span>.</span><span>.</span><span>.</span></span>
        </span>
      </div>
    );
  }

  // Active state: sleek animated bars with opacity modulation
  return (
    <div className="dictation-waveform-container">
      {bars.map((height, i) => (
        <div
          key={i}
          className="dictation-waveform-bar"
          style={{
            height: `${Math.max(2, height * 24)}px`,
            opacity: 0.35 + height * 0.65,
          }}
        />
      ))}
    </div>
  );
};

export default DictationWaveform;